// Global Instructions Rule Applied!
//
// Configuration → Pipeline Statuses: admin CRUD over cfg.pipeline_statuses
// (the decision statuses attached to pipeline stages), migrated out of
// lookup_details. Chrome copied from IndustriesPanel (the newest
// usage-count + replace-flow sibling), simplified: the list is small
// (~8 per category), so a category Segmented filter replaces pagination.
//
// Deleting an in-use status is refused by the backend with a 409 and routed
// into the Replace modal (reassign-and-replace repoints every reference —
// stages, comms LP rules, follower-agent criteria, app_config lists —
// atomically, same-category only). After every write the global-configs
// cache refreshes so status dropdowns across the app update immediately.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Modal, Popconfirm, Segmented, Select, Space, Switch, Table, Tag, Tooltip, Typography } from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, SwapOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import {
  type PipelineStatusAdminRow,
} from '../../../state_manager/data_models/pipeline_statuses_config_model.ts'
import {
  createPipelineStatus,
  deletePipelineStatus,
  fetchPipelineStatuses,
  reassignPipelineStatuses,
  StatusInUseError,
  updatePipelineStatus
} from '../services/pipelineStatusesConfigService.ts'
import { ReplacePipelineStatusModal } from './ReplacePipelineStatusModal.tsx'
import { refreshBoardConfigs } from '../services/refreshBoardConfigs.ts'

const { Title, Text } = Typography

export default function PipelineStatusesPanel() {
  const [rows, setRows] = useState<PipelineStatusAdminRow[]>([])
  const [loading, setLoading] = useState(false)
  // The selected pipeline kind, as a cfg.pipeline_types id; seeded from the
  // first kind once globalConfigs has loaded.
  const [category, setCategory] = useState<string>('')

  // Add / rename modal
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const [editing, setEditing] = useState<PipelineStatusAdminRow | null>(null)
  const [nameValue, setNameValue] = useState('')
  const [modalCategory, setModalCategory] = useState<string>('')
  const [outcomeValue, setOutcomeValue] = useState<string>('')
  const [terminalValue, setTerminalValue] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState(false)

  // Replace / merge modal
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [replaceInitialSources, setReplaceInitialSources] = useState<string[]>([])
  const [reassigning, setReassigning] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const token = () => getAccessToken(store.getState().auth)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setRows(await fetchPipelineStatuses(token()))
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load pipeline statuses')
    } finally {
      setLoading(false)
    }
  }, [])

  // Pipeline kinds and outcomes come from globalConfigs now: both are cfg
  // tables (cfg.pipeline_types, cfg.pipeline_outcomes), so adding a kind or an
  // outcome needs no change here.
  const pipelineTypes = useSelector(
    (state: any) => (state.globalConfigs?.lookup_headers || [])
      .find((h: any) => h?.category === 'Pipeline Type')?.details ?? []
  )
  const pipelineOutcomes = useSelector(
    (state: any) => (state.globalConfigs?.pipeline_outcomes || []) as any[]
  )

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!category && pipelineTypes.length > 0) setCategory(String(pipelineTypes[0].id))
  }, [category, pipelineTypes])

  const visibleRows = useMemo(
    () => rows.filter((r) => r.pipeline_statuses__pipeline_type_id === category),
    [rows, category]
  )

  const outcomesForKind = useMemo(
    () => pipelineOutcomes.filter((o) => String(o?.pipeline_type_id) === String(modalCategory)),
    [pipelineOutcomes, modalCategory]
  )

  const openAdd = () => {
    setEditing(null)
    setNameValue('')
    setModalCategory(category)
    setOutcomeValue('')
    setTerminalValue(false)
    setNameModalOpen(true)
  }

  const openRename = (row: PipelineStatusAdminRow) => {
    setEditing(row)
    setNameValue(row.pipeline_statuses__name)
    setModalCategory(row.pipeline_statuses__pipeline_type_id)
    setOutcomeValue(row.pipeline_statuses__outcome_id ?? '')
    setTerminalValue(row.pipeline_statuses__is_terminal)
    setNameModalOpen(true)
  }

  const submitName = async () => {
    const name = nameValue.trim()
    if (!name) return
    setSubmitting(true)
    try {
      if (editing) {
        await updatePipelineStatus(
          editing.pipeline_statuses__id,
          { name, outcome_id: outcomeValue || null, is_terminal: terminalValue },
          token()
        )
        toast.success('Status renamed')
      } else {
        await createPipelineStatus(
          {
            name,
            pipeline_type_id: modalCategory,
            outcome_id: outcomeValue || null,
            is_terminal: terminalValue
          },
          token()
        )
        toast.success('Status added')
      }
      setNameModalOpen(false)
      await load()
      refreshBoardConfigs()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSubmitting(false)
    }
  }

  const onDelete = async (row: PipelineStatusAdminRow) => {
    setDeletingId(row.pipeline_statuses__id)
    try {
      await deletePipelineStatus(row.pipeline_statuses__id, token())
      toast.success('Status deleted')
      await load()
      refreshBoardConfigs()
    } catch (e) {
      if (e instanceof StatusInUseError) {
        // In-use → the Replace flow, pre-seeded with this status as source.
        setReplaceInitialSources([row.pipeline_statuses__id])
        setReplaceOpen(true)
      } else {
        showErrorToast(e instanceof Error ? e.message : 'Delete failed')
      }
    } finally {
      setDeletingId(null)
    }
  }

  const onReassign = async (sourceIds: string[], targetId: string, deleteSources: boolean) => {
    setReassigning(true)
    try {
      await reassignPipelineStatuses(sourceIds, targetId, deleteSources, token())
      toast.success('References reassigned')
      setReplaceOpen(false)
      await load()
      refreshBoardConfigs()
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Reassign failed')
    } finally {
      setReassigning(false)
    }
  }

  const columns: ColumnsType<PipelineStatusAdminRow> = [
    {
      title: 'Status',
      dataIndex: 'pipeline_statuses__name',
      key: 'name',
      sorter: (a, b) => a.pipeline_statuses__name.localeCompare(b.pipeline_statuses__name),
      render: (name: string) => <Text>{name}</Text>
    },
    {
      // What the process ends as, and whether it has ended. Two facts, shown
      // together because they only make sense together: a live status carries
      // the outcome it would resolve to when a fund closes under it.
      title: 'Outcome',
      key: 'outcome',
      width: 240,
      render: (_: unknown, r: PipelineStatusAdminRow) =>
        r.pipeline_outcomes__label ? (
          <span className='inline-flex items-center gap-2'>
            <Tag color={r.pipeline_outcomes__tag_color ?? 'default'} style={{ marginInlineEnd: 0 }}>
              {r.pipeline_outcomes__label}
            </Tag>
            {r.pipeline_statuses__is_terminal ? (
              <Text type='secondary' style={{ fontSize: 12 }}>ends the process</Text>
            ) : (
              <Text type='secondary' style={{ fontSize: 12 }}>on close</Text>
            )}
          </span>
        ) : (
          <Text type='secondary'>—</Text>
        )
    },
    {
      title: 'Used by',
      dataIndex: 'pipeline_statuses__usage_count',
      key: 'usage',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.pipeline_statuses__usage_count - b.pipeline_statuses__usage_count,
      render: (count: number) =>
        count > 0 ? (
          <Tooltip title='Stages, LP status rules and agent criteria referencing this status'>
            <Tag>{count} reference{count === 1 ? '' : 's'}</Tag>
          </Tooltip>
        ) : (
          <Text type='secondary'>unused</Text>
        )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Space size='small'>
          <Tooltip title='Rename'>
            <Button type='text' size='small' icon={<EditOutlined />} onClick={() => openRename(row)} />
          </Tooltip>
          <Popconfirm
            title={
              row.pipeline_statuses__usage_count > 0
                ? 'This status is in use — deleting opens the Replace flow.'
                : 'Delete this status?'
            }
            okText={row.pipeline_statuses__usage_count > 0 ? 'Replace…' : 'Delete'}
            onConfirm={() => onDelete(row)}
          >
            <Button
              type='text'
              size='small'
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === row.pipeline_statuses__id}
            />
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div className='flex flex-col h-full min-h-0'>
      <div className='flex items-center justify-between flex-wrap gap-2 mb-3'>
        <Title level={4} style={{ margin: 0 }}>
          Pipeline Statuses
        </Title>
        <Space>
          <Segmented
            value={category}
            onChange={(val) => setCategory(String(val))}
            options={pipelineTypes.map((t: any) => ({ value: String(t.id), label: t.label }))}
          />
          <Button
            icon={<SwapOutlined />}
            onClick={() => {
              setReplaceInitialSources([])
              setReplaceOpen(true)
            }}
          >
            Replace / Merge
          </Button>
          <Button type='primary' icon={<PlusOutlined />} onClick={openAdd}>
            New Status
          </Button>
        </Space>
      </div>

      <div className='flex-1 min-h-0 overflow-auto'>
        <Table
          size='small'
          rowKey='pipeline_statuses__id'
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          pagination={false}
        />
      </div>

      <Modal
        title={editing ? 'Rename status' : 'New status'}
        open={nameModalOpen}
        okText={editing ? 'Rename' : 'Add'}
        okButtonProps={{ disabled: !nameValue.trim() }}
        confirmLoading={submitting}
        onOk={submitName}
        onCancel={() => setNameModalOpen(false)}
        destroyOnClose
      >
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          <div>
            <Text strong>Name</Text>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onPressEnter={submitName}
              placeholder='Status name'
              style={{ marginTop: 4 }}
              autoFocus
            />
          </div>
          <div>
            <Text strong>Pipeline kind</Text>
            <Select
              value={modalCategory}
              onChange={(val) => setModalCategory(String(val))}
              options={pipelineTypes.map((t: any) => ({ value: String(t.id), label: t.label }))}
              disabled={!!editing}
              style={{ width: '100%', marginTop: 4 }}
            />
            {editing && (
              <Text type='secondary' style={{ fontSize: 12 }}>
                The pipeline kind is fixed once a status exists.
              </Text>
            )}
          </div>
          <div>
            <Text strong>Outcome</Text>
            <Select
              allowClear
              value={outcomeValue || undefined}
              onChange={(val) => setOutcomeValue(val ? String(val) : '')}
              placeholder='None — the process is still running here'
              options={outcomesForKind.map((o: any) => ({ value: String(o.id), label: o.label }))}
              style={{ width: '100%', marginTop: 4 }}
            />
            <Text type='secondary' style={{ fontSize: 12 }}>
              What a process ENDS AS at this status. Set it on live statuses too:
              it is what they resolve to when a fund closes under them.
            </Text>
          </div>
          <div>
            <Text strong>Ends the process</Text>
            <div style={{ marginTop: 4 }}>
              <Switch checked={terminalValue} onChange={setTerminalValue} />
              <Text type='secondary' style={{ fontSize: 12, marginLeft: 8 }}>
                On: the outcome shows immediately. Off: only once the fund closes.
              </Text>
            </div>
          </div>
        </Space>
      </Modal>

      <ReplacePipelineStatusModal
        open={replaceOpen}
        statuses={rows}
        initialSourceIds={replaceInitialSources}
        confirmLoading={reassigning}
        onConfirm={onReassign}
        onCancel={() => setReplaceOpen(false)}
      />
    </div>
  )
}
